import React, { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GbpAutoReply } from "@/components/GbpAutoReply";
import { WhatsAppButtonInstall } from "@/components/WhatsAppButtonInstall";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import CmsIntegrationPanel from "@/components/CmsIntegrationPanel";
import ChannelAccountsSection from "@/components/ChannelAccountsSection";
import {
  WhatsappLogo, FacebookLogo, InstagramLogo, MapPin,
  Database, CheckCircle, XCircle, Warning, ArrowClockwise, Plugs, PlugsConnected,
  Lightning,
} from "@phosphor-icons/react";

const PROVIDER_ICONS = {
  whatsapp: WhatsappLogo,
  messenger: FacebookLogo,
  instagram: InstagramLogo,
  google_business: MapPin,
  cms: Database,
  crm_webhook: Lightning,
};

const PROVIDER_COLORS = {
  whatsapp: "text-emerald-500",
  messenger: "text-blue-500",
  instagram: "text-pink-500",
  google_business: "text-red-500",
  cms: "text-cyan-500",
  crm_webhook: "text-orange-500",
};

export default function Integrations({ embedded = false }) {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(() => (new URLSearchParams(window.location.search).get("gbp") ? "google_business" : null));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/overview");
      setOverview(data);
    } catch {
      toast.error("Error cargando integraciones");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      {!embedded && <Topbar />}
      <div className={embedded ? "" : "p-8 flex-1 overflow-y-auto min-h-0"} data-testid="integrations-page">
        <div className={embedded ? "" : "max-w-7xl mx-auto"}>
          {!embedded && (
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="overline mb-2">SDM · Conectividad</div>
              <h1 className="font-heading text-3xl font-bold tracking-tight">Integraciones</h1>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Conecta canales de comunicación (email, WhatsApp, Meta, Google My Business) y
                sistemas internos (CMS custom). Todo se centraliza en la Bandeja Omnicanal.
              </p>
            </div>
            <Button variant="outline" onClick={load} data-testid="integrations-refresh">
              <ArrowClockwise size={14} className="mr-2" /> Actualizar
            </Button>
          </div>
          )}

          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {loading && (
              <div className="col-span-full text-center py-12 text-muted-foreground">Cargando…</div>
            )}
            {overview.map((it) => {
              const Icon = PROVIDER_ICONS[it.key] || Plugs;
              const iconColor = PROVIDER_COLORS[it.key] || "text-primary";
              const connected = it.connected;
              return (
                <button
                  key={it.key}
                  onClick={() => setActiveKey(it.key)}
                  data-testid={`integration-card-${it.key}`}
                  className={`text-left border rounded-lg p-5 bg-card hover:bg-accent/30 transition-colors ${
                    connected ? "border-emerald-500/40" : "border-border"
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-11 h-11 rounded-md flex items-center justify-center ${
                      connected ? "bg-emerald-500/10" : "bg-secondary/60"
                    }`}>
                      <Icon size={22} weight="fill" className={iconColor} />
                    </div>
                    <Badge className={connected ? "bg-emerald-500 text-white gap-1" : "bg-muted text-muted-foreground gap-1"}>
                      {connected ? <><PlugsConnected size={11} weight="fill" /> Conectado</> : <><Plugs size={11} /> No conectado</>}
                    </Badge>
                  </div>
                  <h3 className="font-heading font-bold text-base leading-tight mb-1">{it.label}</h3>
                  <p className="text-xs text-muted-foreground">{it.subtitle}</p>
                  {it.last_error && (
                    <div className="mt-2 text-[11px] text-rose-500 flex items-center gap-1">
                      <Warning size={11} /> {it.last_error}
                    </div>
                  )}
                  {it.last_synced_at && (
                    <div className="mt-2 text-[10px] text-muted-foreground">
                      Última sync: {new Date(it.last_synced_at).toLocaleString("es-DO")}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Detail panels */}
          {activeKey === "whatsapp" && (
            <>
              <ChannelAccountsSection channel="whatsapp" onChange={load} />
              <MetaChannelPanel channel="whatsapp" onChange={load} />
            </>
          )}
          {activeKey === "messenger" && (
            <>
              <ChannelAccountsSection channel="messenger" onChange={load} />
              <MetaChannelPanel channel="messenger" onChange={load} />
            </>
          )}
          {activeKey === "instagram" && (
            <>
              <ChannelAccountsSection channel="instagram" onChange={load} />
              <MetaChannelPanel channel="instagram" onChange={load} />
            </>
          )}
          {activeKey === "google_business" && <GmbPanel onChange={load} />}
          {activeKey === "cms" && (
            <div className="mt-4">
              <CmsIntegrationPanel />
            </div>
          )}
          {activeKey === "crm_webhook" && (
            <div className="mt-4">
              <CrmWebhookPanel onChange={load} />
            </div>
          )}

          <SandboxNotice />
        </div>
      </div>
    </>
  );
}

function SandboxNotice() {
  return (
    <div className="mt-12 border border-amber-500/30 bg-amber-500/5 rounded-lg p-4 text-xs">
      <div className="flex items-center gap-2 font-semibold text-amber-600 mb-1">
        <Warning size={14} weight="fill" /> Modo sandbox
      </div>
      <p className="text-muted-foreground leading-relaxed">
        Las integraciones externas (Meta, Google My Business)
        están en <strong>sandbox</strong>: puedes guardar tokens y probar el flujo, pero los mensajes reales requieren
        credenciales de producción en <code className="bg-muted rounded px-1">.env</code> del backend.
        CMS Custom ya está en modo dev conectado a Azure.
      </p>
    </div>
  );
}

/* ================= META DM TOOLS (Messenger / IG) ================= */
function MetaDMTools({ channel }) {
  const [status, setStatus] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [simFrom, setSimFrom] = useState(channel === "instagram" ? "IG_TEST_USER_1" : "PSID_TEST_USER_1");
  const [simText, setSimText] = useState("Hola, quiero cotizar una Toyota Hilux");
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);
  useEffect(() => { api.get("/instagram/status").then(({ data }) => setStatus(data)).catch(() => {}); }, [channel]);

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await api.post(`/integrations/meta/${channel}/subscribe`);
      toast[data.ok ? "success" : "error"](data.ok ? `Webhooks suscritos · ${data.fields}` : (data.error || "Error"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSubscribing(false); }
  };
  const simulate = async () => {
    setSimulating(true); setSimResult(null);
    try {
      const { data } = await api.post("/instagram/simulate", { from: simFrom, text: simText, channel });
      setSimResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error del simulador"); } finally { setSimulating(false); }
  };
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copiado"); };
  const url = status?.webhook_url;

  return (
    <div className="mx-5 mb-4 space-y-3" data-testid={`${channel}-dm-tools`}>
      <div className="border border-border rounded-md p-3 bg-secondary/30 text-xs space-y-2">
        <div className="font-semibold">Webhook de Meta</div>
        <div className="flex items-center gap-2">
          <code className="font-mono text-[11px] bg-background/60 px-2 py-1 rounded flex-1 truncate" data-testid={`${channel}-webhook-url`}>{url || "…"}</code>
          <Button size="sm" variant="outline" className="h-7" onClick={() => url && copy(url)} data-testid={`${channel}-webhook-copy`}>Copiar</Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          En developers.facebook.com → tu app → {channel === "instagram" ? "Instagram → Webhooks" : "Messenger → Webhooks"}: pega esta URL y el <em>Webhook verify token</em> de arriba. Suscribe los campos <code>messages</code> y <code>messaging_postbacks</code>, luego pulsa “Suscribir webhooks”.
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={subscribe} disabled={subscribing} data-testid={`${channel}-subscribe`}>{subscribing ? "…" : "Suscribir webhooks"}</Button>
          {status && !status.app_secret_configured && (
            <span className="text-[11px] text-amber-600" data-testid={`${channel}-secret-warning`}>Falta App Secret — los webhooks firmados serán rechazados en producción.</span>
          )}
        </div>
      </div>
      <div className="border border-sky-500/30 bg-sky-500/5 rounded-md p-3">
        <div className="text-xs font-semibold text-sky-600 mb-2">Simulador de DM entrante (bot)</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input value={simFrom} onChange={(e) => setSimFrom(e.target.value)} className="font-mono text-sm" data-testid={`${channel}-sim-from`} />
          <Input value={simText} onChange={(e) => setSimText(e.target.value)} className="text-sm md:col-span-2" data-testid={`${channel}-sim-text`} />
        </div>
        <Button size="sm" variant="outline" className="mt-2" onClick={simulate} disabled={simulating} data-testid={`${channel}-sim-send`}>
          {simulating ? "Procesando…" : "Simular mensaje entrante"}
        </Button>
        {simResult && (
          <div className="mt-2 text-xs bg-secondary border border-border rounded-md p-2 whitespace-pre-wrap" data-testid={`${channel}-sim-result`}>
            <div className="text-[10px] text-primary font-semibold mb-1">SDM Concierge</div>
            {simResult.reply || "(bot pausado — un agente tiene la conversación)"}
            <div className="text-[10px] text-muted-foreground mt-1">
              {simResult.sandbox ? "Sandbox — no enviado a Meta (falta token)" : simResult.send?.ok ? "Enviado vía Meta ✓" : `Meta: ${simResult.send?.error || "—"}`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= META CHANNELS (WA / Messenger / IG) ================= */
function MetaChannelPanel({ channel, onChange }) {
  const label = { whatsapp: "WhatsApp Business", messenger: "Facebook Messenger", instagram: "Instagram Direct" }[channel];
  const Icon = { whatsapp: WhatsappLogo, messenger: FacebookLogo, instagram: InstagramLogo }[channel];
  const color = PROVIDER_COLORS[channel];
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [pingTo, setPingTo] = useState("");
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [pingLogs, setPingLogs] = useState([]);
  const [simFrom, setSimFrom] = useState("18091234567");
  const [simText, setSimText] = useState("Hola, quiero información de una Cadillac Escalade");
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/integrations/meta/${channel}`); setCfg(data); } catch { setCfg({}); }
    if (channel === "whatsapp") {
      try {
        const { data } = await api.get("/integrations/meta/whatsapp/test-logs?limit=10");
        setPingLogs(data);
      } catch {
        /* no logs yet */
      }
    }
  };
  useEffect(() => { load(); }, [channel]);

  if (!cfg) return null;

  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/integrations/meta/${channel}`, cfg);
      setCfg(data);
      toast.success("Guardado — verificando con Meta…");
      onChange?.();
      // Auto-run the /test endpoint so the user immediately knows if the token is valid.
      try {
        const { data: t } = await api.post(`/integrations/meta/${channel}/test`);
        setTestResult(t);
        if (t.ok) {
          toast.success("Meta validó el token ✓");
        } else {
          toast.error(t.error || "Meta rechazó el token");
        }
        // Reload cfg so last_error / last_verified_at refresh in the UI
        try { const { data: fresh } = await api.get(`/integrations/meta/${channel}`); setCfg(fresh); } catch { /* ignore */ }
      } catch { /* verify is best-effort */ }
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };
  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post(`/integrations/meta/${channel}/test`);
      setTestResult(data);
      toast[data.ok ? "success" : "error"](data.ok ? "Meta OK" : (data.error || "Error"));
      onChange?.();
    } catch { toast.error("Error"); } finally { setTesting(false); }
  };
  const sendPing = async () => {
    if (!pingTo.trim()) return toast.error("Ingresa un número destino, ej +18095551234");
    setPinging(true); setPingResult(null);
    try {
      const { data } = await api.post("/integrations/meta/whatsapp/send-test", { to: pingTo, template: "hello_world", language: "en_US" });
      setPingResult(data);
      toast[data.ok ? "success" : "error"](data.ok ? `Enviado · id ${data.message_id?.slice(0, 12)}…` : (data.error || data.meta_response?.error?.message || "Error"));
      try {
        const r = await api.get("/integrations/meta/whatsapp/test-logs?limit=10");
        setPingLogs(r.data);
      } catch { /* ignore */ }
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setPinging(false); }
  };
  const runSimulate = async () => {
    if (!simFrom.trim() || !simText.trim()) return toast.error("Completa número y mensaje");
    setSimulating(true); setSimResult(null);
    try {
      const { data } = await api.post("/whatsapp/simulate", { from: simFrom.replace(/[^0-9]/g, ""), text: simText });
      setSimResult(data);
      toast.success("Bot respondió · ver más abajo");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error del simulador"); } finally { setSimulating(false); }
  };

  return (
    <div className="border border-border rounded-lg bg-card mt-4" data-testid={`${channel}-panel`}>
      <div className="p-5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon size={22} weight="fill" className={color} />
          <div>
            <h3 className="font-heading font-bold text-lg">{label}</h3>
            <p className="text-xs text-muted-foreground">Meta Business API · webhook + Graph API</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => patch("enabled", v)} data-testid={`${channel}-enabled`} />
          <span className="text-xs uppercase">{cfg.enabled ? "Activo" : "Deshabilitado"}</span>
        </div>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Access token</Label>
          <Input type="password" value={cfg.access_token || ""} onChange={(e) => patch("access_token", e.target.value)} placeholder="EAAG..." data-testid={`${channel}-token`} className="font-mono text-xs" />
          {cfg.access_token && !/^\*+/.test(cfg.access_token) && !/(EAA|IGAA|IGQV)[A-Za-z0-9_-]{20,}/.test(cfg.access_token) && (
            <div className="mt-1 text-[10px] text-amber-500 flex items-start gap-1" data-testid={`${channel}-token-warning`}>
              <Warning size={11} weight="fill" className="mt-0.5 flex-shrink-0"/>
              <span>El token debe empezar con <code>EAA</code> (Page token) o <code>IGAA</code> (Instagram Login). Si copiaste &ldquo;Access Token EAA…&rdquo; del panel de Meta, el backend intentará extraer solo la parte válida al guardar, pero es más seguro pegar solo el token.</span>
            </div>
          )}
        </div>
        <div>
          <Label>Webhook verify token</Label>
          <Input value={cfg.webhook_verify_token || ""} onChange={(e) => patch("webhook_verify_token", e.target.value)} data-testid={`${channel}-verify-token`} className="font-mono text-xs" />
        </div>
        {channel === "whatsapp" && (
          <>
            <div><Label>Phone number ID</Label><Input value={cfg.phone_number_id || ""} onChange={(e) => patch("phone_number_id", e.target.value)} data-testid="wa-phone-id" /></div>
            <div><Label>WABA ID</Label><Input value={cfg.business_account_id || ""} onChange={(e) => patch("business_account_id", e.target.value)} data-testid="wa-waba" /></div>
            <div>
              <Label>Plantilla recordatorio de cita (opcional)</Label>
              <Input value={cfg.reminder_template_name || ""} onChange={(e) => patch("reminder_template_name", e.target.value)} placeholder="recordatorio_cita_taller" data-testid="wa-reminder-template" className="font-mono text-xs" />
              <p className="text-[10px] text-muted-foreground mt-1">Plantilla aprobada en Meta con 4 variables: {"{{1}}"} nombre · {"{{2}}"} fecha · {"{{3}}"} hora · {"{{4}}"} sucursal. Vacío = texto libre (solo llega si el cliente escribió en las últimas 24h).</p>
            </div>
            <div><Label>Idioma de la plantilla</Label><Input value={cfg.reminder_template_lang || "es"} onChange={(e) => patch("reminder_template_lang", e.target.value)} data-testid="wa-reminder-template-lang" className="font-mono text-xs" /></div>
          </>
        )}
        {channel === "messenger" && (
          <div><Label>Page ID</Label><Input value={cfg.page_id || ""} onChange={(e) => patch("page_id", e.target.value)} data-testid="fb-page-id" /></div>
        )}
        {channel === "instagram" && (
          <div><Label>IG Account ID</Label><Input value={cfg.ig_account_id || ""} onChange={(e) => patch("ig_account_id", e.target.value)} data-testid="ig-account-id" /></div>
        )}
        {channel !== "whatsapp" && (
          <div>
            <Label>App Secret (firma del webhook)</Label>
            <Input type="password" value={cfg.app_secret || ""} onChange={(e) => patch("app_secret", e.target.value)} data-testid={`${channel}-app-secret`} className="font-mono text-xs" />
          </div>
        )}
      </div>
      {channel !== "whatsapp" && <MetaDMTools channel={channel} />}
      {channel === "whatsapp" && <WhatsAppButtonInstall />}

      {cfg.last_error && (
        <div className="mx-5 mb-3 border border-rose-500/40 bg-rose-500/10 rounded-md p-3 text-xs" data-testid={`${channel}-last-error-banner`}>
          <div className="flex items-start gap-2">
            <Warning size={16} weight="fill" className="text-rose-500 mt-0.5 flex-shrink-0"/>
            <div className="flex-1">
              <div className="font-semibold text-rose-500 mb-1">Meta rechazó la última operación</div>
              <div className="text-rose-500/90 font-mono text-[11px] leading-relaxed break-all">{cfg.last_error}</div>
              {cfg.last_error_at && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  {new Date(cfg.last_error_at).toLocaleString("es-DO")}
                </div>
              )}
              {/session has expired|expir[eé]|OAuth|code[^\d]*190|HTTP 401/i.test(cfg.last_error) && (
                <div className="mt-2 pt-2 border-t border-rose-500/20 text-[11px] text-foreground">
                  <strong>Cómo solucionarlo:</strong>
                  <ol className="list-decimal ml-4 mt-1 space-y-0.5 text-muted-foreground">
                    <li>Ve a <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-emerald-500 underline">developers.facebook.com</a> → tu app → WhatsApp → API Setup.</li>
                    <li>Copia el nuevo <em>Temporary access token</em> (o genera uno permanente vía System User).</li>
                    <li>Pégalo arriba en <em>Access token</em> y presiona <em>Guardar</em>.</li>
                    <li>Presiona <em>Probar conexión Meta</em> para validar.</li>
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {testResult && (
        <div className="mx-5 mb-3 border border-border rounded-md p-3 bg-secondary/40 text-xs" data-testid={`${channel}-test-result`}>
          <div className="flex items-center gap-2 mb-2">
            {testResult.ok
              ? <><CheckCircle size={14} weight="fill" className="text-emerald-500"/><span className="font-semibold text-emerald-500">Meta responde OK</span></>
              : <><XCircle size={14} weight="fill" className="text-rose-500"/><span className="font-semibold text-rose-500">Error de Meta</span></>}
            {testResult.http_status && <Badge variant="outline">HTTP {testResult.http_status}</Badge>}
          </div>
          {testResult.error && <div className="text-rose-500 mb-2">{testResult.error}</div>}
          {testResult.meta_account && (
            <pre className="text-[10px] font-mono bg-background/60 p-2 rounded overflow-auto max-h-40">{JSON.stringify(testResult.meta_account, null, 2)}</pre>
          )}
        </div>
      )}

      {channel === "whatsapp" && (
        <div className="mx-5 mb-5 border border-emerald-500/30 bg-emerald-500/5 rounded-md p-3" data-testid="whatsapp-ping-tool">
          <div className="text-xs font-semibold text-emerald-600 mb-1 flex items-center gap-1.5">
            <WhatsappLogo size={13} weight="fill" /> Enviar ping de prueba (template `hello_world`)
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">
            Manda el template gratuito <code>hello_world</code> a un WhatsApp real. El número debe estar registrado como tester o el WABA debe estar en producción.
          </p>
          <div className="flex gap-2">
            <Input
              value={pingTo}
              onChange={(e) => setPingTo(e.target.value)}
              placeholder="+18095551234"
              data-testid="wa-ping-to"
              className="font-mono text-sm flex-1"
            />
            <Button onClick={sendPing} disabled={pinging} data-testid="wa-ping-send">
              {pinging ? "…" : "Enviar ping"}
            </Button>
          </div>
          {pingResult && (
            <div className="mt-2 text-[11px]" data-testid="wa-ping-result">
              {pingResult.ok
                ? <div className="text-emerald-600">✓ Message ID: <code className="font-mono">{pingResult.message_id}</code></div>
                : <div className="text-rose-500">✗ {pingResult.error || pingResult.meta_response?.error?.message}</div>}
              {pingResult.meta_response && (
                <pre className="text-[10px] font-mono bg-background/60 p-2 rounded overflow-auto max-h-32 mt-1">{JSON.stringify(pingResult.meta_response, null, 2)}</pre>
              )}
            </div>
          )}
          {pingLogs.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Últimos pings</div>
              <div className="space-y-1">
                {pingLogs.map((l) => (
                  <div key={l.id} className="text-[10px] flex items-center gap-2" data-testid={`wa-ping-log-${l.id}`}>
                    {l.success ? <CheckCircle size={11} weight="fill" className="text-emerald-500"/> : <XCircle size={11} weight="fill" className="text-rose-500"/>}
                    <span className="font-mono">{l.to}</span>
                    <span className="text-muted-foreground">HTTP {l.http_status}</span>
                    <span className="text-muted-foreground ml-auto">{new Date(l.created_at).toLocaleString("es-DO")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {channel === "whatsapp" && (
        <div className="mx-5 mb-5 border border-sky-500/30 bg-sky-500/5 rounded-md p-3" data-testid="whatsapp-sim-tool">
          <div className="text-xs font-semibold text-sky-600 mb-1 flex items-center gap-1.5">
            <WhatsappLogo size={13} weight="fill" /> Simulador sandbox (sin Meta)
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">
            Prueba el bot end-to-end sin token de Meta válido. Simula un mensaje entrante — la IA procesa (tools + RAG + memoria) y crea la conversación en el Inbox.
            El mensaje saliente se registra en logs (no se envía por WhatsApp real).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input
              value={simFrom}
              onChange={(e) => setSimFrom(e.target.value)}
              placeholder="18091234567 (número simulado)"
              data-testid="wa-sim-from"
              className="font-mono text-sm"
            />
            <Input
              value={simText}
              onChange={(e) => setSimText(e.target.value)}
              placeholder="Mensaje del cliente"
              data-testid="wa-sim-text"
              className="text-sm md:col-span-2"
            />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Button onClick={runSimulate} disabled={simulating} data-testid="wa-sim-send" variant="outline" size="sm">
              {simulating ? "Procesando…" : "Simular mensaje entrante"}
            </Button>
            <span className="text-[10px] text-muted-foreground">Aparecerá en Inbox como conversación de canal WhatsApp</span>
          </div>
          {simResult && (
            <div className="mt-3 space-y-2" data-testid="wa-sim-result">
              <div className="flex justify-end">
                <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg rounded-tr-none px-3 py-2 max-w-[80%]">
                  <div className="text-[10px] text-emerald-700 dark:text-emerald-400 font-semibold mb-0.5">Cliente ({simFrom})</div>
                  <div className="text-xs text-foreground whitespace-pre-wrap">{simText}</div>
                </div>
              </div>
              <div className="flex justify-start">
                <div className="bg-secondary border border-border rounded-lg rounded-tl-none px-3 py-2 max-w-[80%]">
                  <div className="text-[10px] text-primary font-semibold mb-0.5">SDM Concierge</div>
                  <div className="text-xs text-foreground whitespace-pre-wrap">{simResult.reply}</div>
                  {simResult.sandbox && (
                    <div className="text-[10px] text-muted-foreground mt-1">
                      <Warning size={10} weight="fill" className="inline mr-1"/>
                      Modo sandbox — respuesta no enviada por WhatsApp real
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="p-5 border-t border-border flex items-center gap-2">
        <Button onClick={save} disabled={saving} data-testid={`${channel}-save`}>{saving ? "Guardando…" : "Guardar"}</Button>
        <Button variant="outline" onClick={runTest} disabled={testing} data-testid={`${channel}-test`}>{testing ? "…" : "Probar conexión Meta"}</Button>
        {cfg.last_verified_at && (
          <span className="text-[11px] text-muted-foreground ml-auto">
            Verificado: {new Date(cfg.last_verified_at).toLocaleString("es-DO")}
          </span>
        )}
      </div>
    </div>
  );
}

/* ================= GMB ================= */
function GmbPanel({ onChange }) {
  const [cfg, setCfg] = useState(null);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = async () => { try { const { data } = await api.get("/integrations/google-business"); setCfg(data); } catch { setCfg({}); } };
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("gbp") === "connected") toast.success("Google conectado. Ahora pulsa “Sincronizar sucursales”.");
  }, []);
  const loadOauth = async () => {
    try { const { data } = await api.get("/integrations/google-business/status"); setOauthStatus(data); }
    catch { setOauthStatus({ configured: false, connected: false }); }
  };
  useEffect(() => { load(); loadOauth(); }, []);
  if (!cfg) return null;
  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    try { const { data } = await api.patch("/integrations/google-business", cfg); setCfg(data); toast.success("Guardado"); onChange?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const runTest = async () => {
    try { const { data } = await api.post("/integrations/google-business/test"); toast[data.ok ? "success" : "error"](data.message || data.error || "Error"); onChange?.(); } catch { toast.error("Error"); }
  };
  const connectOAuth = async () => {
    try {
      const { data } = await api.get("/integrations/google-business/connect");
      if (data.authorize_url) window.location.href = data.authorize_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "OAuth no configurado — pídele al developer que configure GOOGLE_CLIENT_ID/SECRET en backend/.env"); }
  };
  const syncLocations = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/gbp/locations/sync"); toast.success(`${data.synced} sucursales sincronizadas`); loadOauth(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSyncing(false); }
  };
  const syncReviews = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/gbp/reviews/sync"); toast.success(`${data.new_reviews} reseñas nuevas`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSyncing(false); }
  };
  return (
    <div className="border border-border rounded-lg bg-card mt-4" data-testid="gmb-panel">
      <div className="p-5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MapPin size={22} weight="fill" className={PROVIDER_COLORS.google_business} />
          <div>
            <h3 className="font-heading font-bold text-lg">Google Business Profile · Reviews</h3>
            <p className="text-xs text-muted-foreground">Lee y responde reseñas de tus sucursales. Las reseñas negativas se responden automáticamente.</p>
          </div>
        </div>
        <Switch checked={!!cfg.enabled} onCheckedChange={(v) => patch("enabled", v)} data-testid="gmb-enabled" />
      </div>

      {/* OAuth 2.0 connect (Sprint 33G · Feb 2026) */}
      <div className="p-5 border-b border-border bg-secondary/20">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Conexión con Google (OAuth)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <Label>Client ID</Label>
            <Input value={cfg.gbp_client_id || ""} onChange={(e) => patch("gbp_client_id", e.target.value)} placeholder="xxxx.apps.googleusercontent.com" className="font-mono text-xs" data-testid="gmb-client-id" />
          </div>
          <div>
            <Label>Client Secret</Label>
            <Input type="password" value={cfg.gbp_client_secret || ""} onChange={(e) => patch("gbp_client_secret", e.target.value)} placeholder="GOCSPX-…" className="font-mono text-xs" data-testid="gmb-client-secret" />
          </div>
          <div className="md:col-span-2">
            <Label>URI de redireccionamiento (pégala en Google Cloud → Credenciales → ID de cliente OAuth)</Label>
            <div className="flex gap-2">
              <code className="flex-1 truncate font-mono text-[11px] bg-background/60 border border-border rounded px-2 py-2" data-testid="gmb-redirect-uri">{oauthStatus?.redirect_uri || "…"}</code>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(oauthStatus?.redirect_uri || ""); toast.success("Copiado"); }} data-testid="gmb-redirect-copy">Copiar</Button>
              <Button size="sm" onClick={async () => { await save(); loadOauth(); }} data-testid="gmb-oauth-save">Guardar credenciales</Button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="text-sm font-medium">
              {oauthStatus?.connected
                ? <span className="text-emerald-600">✓ Conectado a Google · {oauthStatus?.locations_count || 0} sucursales</span>
                : oauthStatus?.configured
                  ? <span className="text-amber-600">Pendiente conectar</span>
                  : <span className="text-slate-500">Falta Client ID / Client Secret — modo simulación</span>}
            </div>
            {oauthStatus?.note && <div className="text-[11px] text-muted-foreground mt-1">{oauthStatus.note}</div>}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={connectOAuth} disabled={!oauthStatus?.configured} data-testid="gmb-oauth-connect">
              {oauthStatus?.connected ? "Reconectar" : "Conectar Google"}
            </Button>
            {oauthStatus?.connected && (
              <>
                <Button variant="outline" onClick={syncLocations} disabled={syncing} data-testid="gmb-sync-locations">
                  Sincronizar sucursales
                </Button>
                <Button variant="outline" onClick={syncReviews} disabled={syncing} data-testid="gmb-sync-reviews">
                  Sincronizar reseñas
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <GbpAutoReply cfg={cfg} patch={patch} onSave={save} />

      {/* Legacy manual config (kept for backward compatibility) */}
      <details className="p-5">
        <summary className="cursor-pointer text-xs uppercase tracking-wider text-muted-foreground">Configuración manual (legacy)</summary>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Account ID</Label><Input value={cfg.account_id || ""} onChange={(e) => patch("account_id", e.target.value)} data-testid="gmb-account" /></div>
          <div><Label>Location ID</Label><Input value={cfg.location_id || ""} onChange={(e) => patch("location_id", e.target.value)} data-testid="gmb-location" /></div>
          <div className="md:col-span-2"><Label>OAuth refresh token</Label><Input type="password" value={cfg.oauth_refresh_token || ""} onChange={(e) => patch("oauth_refresh_token", e.target.value)} data-testid="gmb-oauth" className="font-mono text-xs" /></div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Button onClick={save} data-testid="gmb-save">Guardar</Button>
          <Button variant="outline" onClick={runTest} data-testid="gmb-test">Probar conexión</Button>
        </div>
      </details>
    </div>
  );
}



// ---------- CRM Webhook Panel (Sprint 34K + 34P.5 · dos Zaps: Ventas + Test Drive) ----------
function CrmWebhookPanel({ onChange }) {
  const [cfg, setCfg] = useState({ url: "", url_sales: "", url_test_drive: "", enabled: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ sales: false, test_drive: false });
  const [testResult, setTestResult] = useState({ sales: null, test_drive: null });

  const load = async () => {
    try {
      const { data } = await api.get("/integrations/crm-webhook");
      setCfg({
        url: data?.url || "",
        url_sales: data?.url_sales || "",
        url_test_drive: data?.url_test_drive || "",
        enabled: !!data?.enabled,
        last_test_at_sales: data?.last_test_at_sales,
        last_test_status_sales: data?.last_test_status_sales,
        last_test_at_test_drive: data?.last_test_at_test_drive,
        last_test_status_test_drive: data?.last_test_status_test_drive,
      });
    } catch (e) {
      // Non-admin can't read — silent fail.
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/integrations/crm-webhook", {
        url_sales: cfg.url_sales,
        url_test_drive: cfg.url_test_drive,
        enabled: cfg.enabled,
      });
      setCfg((prev) => ({ ...prev, ...data }));
      toast.success("Configuración guardada");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const runTest = async (target) => {
    setTesting((t) => ({ ...t, [target]: true }));
    setTestResult((r) => ({ ...r, [target]: null }));
    try {
      const url = target === "sales" ? cfg.url_sales : cfg.url_test_drive;
      const { data } = await api.post("/integrations/crm-webhook/test", { url, target });
      setTestResult((r) => ({ ...r, [target]: data }));
      if (data.ok) toast.success(`Test ${target === "sales" ? "Ventas" : "Test Drive"} OK: Zapier respondió 2xx`);
      else toast.error(`Test ${target} falló: ${data.status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setTesting((t) => ({ ...t, [target]: false }));
    }
  };

  const isConnected = !!(cfg.enabled && (cfg.url_sales || cfg.url_test_drive));

  const UrlRow = ({ target, label, description }) => {
    const value = target === "sales" ? cfg.url_sales : cfg.url_test_drive;
    const setValue = (v) => setCfg((c) => ({
      ...c,
      [target === "sales" ? "url_sales" : "url_test_drive"]: v,
    }));
    const testAt = target === "sales" ? cfg.last_test_at_sales : cfg.last_test_at_test_drive;
    const testStatus = target === "sales" ? cfg.last_test_status_sales : cfg.last_test_status_test_drive;
    const localResult = testResult[target];
    return (
      <div className="border border-border rounded-md p-4 bg-background/40" data-testid={`crm-webhook-row-${target}`}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <Label className="text-sm font-semibold">{label}</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>
          </div>
          <Badge className={value ? "bg-emerald-500/15 text-emerald-600 border-transparent" : "bg-slate-500/15 text-slate-600 border-transparent"}>
            {value ? "Configurado" : "Vacío"}
          </Badge>
        </div>
        <Input
          value={value || ""}
          onChange={(e) => setValue(e.target.value)}
          placeholder="https://hooks.zapier.com/hooks/catch/12345678/abcdef/"
          className="font-mono text-xs"
          data-testid={`crm-webhook-url-${target}`}
        />
        {testAt && (
          <div className="text-[10px] text-muted-foreground mt-2 border-l-2 border-border pl-2">
            Último test: {new Date(testAt).toLocaleString("es-DO")} ·{" "}
            <span className={testStatus?.startsWith("2") ? "text-emerald-600" : "text-rose-500"}>
              {testStatus}
            </span>
          </div>
        )}
        {localResult && (
          <div className={`text-[10px] p-2 rounded-md border mt-2 ${localResult.ok ? "border-emerald-500/40 bg-emerald-500/10" : "border-rose-500/40 bg-rose-500/10"}`}>
            <b>{localResult.ok ? "✓ Test OK" : "✗ Test falló"}</b> · {localResult.status}
          </div>
        )}
        <div className="mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => runTest(target)}
            disabled={testing[target] || !value}
            data-testid={`crm-webhook-test-${target}`}
          >
            {testing[target] ? "…" : "Probar envío"}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="border border-border rounded-lg bg-card" data-testid="crm-webhook-panel">
      <div className="p-5 border-b border-border">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="font-heading font-bold text-base">CRM Push · Zapier → Dynamics 365</h3>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
              Envía leads a Dynamics vía dos Zaps separados: uno para <b>Ventas</b> (nuevos + seminuevos)
              y otro para <b>Test Drive</b> (cliente pide prueba de manejo). Payload: <b>Nombre · Apellidos · Contacto · Email ·
              Sucursal · Marca · Modelo</b> + <code>meta.channel</code> (canal de originación:
              whatsapp/web/instagram/messenger/email/sms/voice). Retry automático 3 intentos (1s / 5s / 15s).
              <em className="block mt-2 text-[10px] text-muted-foreground/80">Financiamiento no se pushea a Dynamics — el BDC lo atiende en vivo desde la Bandeja.</em>
            </p>
          </div>
          <Badge className={isConnected ? "bg-emerald-500/15 text-emerald-600 border-transparent" : "bg-slate-500/15 text-slate-600 border-transparent"}>
            {isConnected ? "Conectado" : "Desconectado"}
          </Badge>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <UrlRow
          target="sales"
          label="Zap · Ventas (nuevos + seminuevos)"
          description="Se dispara cuando el bot recolecta un lead comercial (sales_lead)."
        />
        <UrlRow
          target="test_drive"
          label="Zap · Test Drive"
          description="Se dispara cuando el bot registra un lead con test_drive_interest=true (cliente pidió prueba de manejo)."
        />
        <div className="flex items-center gap-3 pt-1">
          <Switch
            checked={!!cfg.enabled}
            onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))}
            data-testid="crm-webhook-enabled"
          />
          <span className="text-xs">Activar envío automático a Zapier</span>
        </div>
      </div>
      <div className="p-5 border-t border-border flex items-center gap-2">
        <Button onClick={save} disabled={saving} data-testid="crm-webhook-save">
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
}

